import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocationParams } from 'hooks';
import { useAdSets } from '../api';
import type { AdSettingsRequestAdSet } from '../api/getAdSettings';
import {
  collectUniqueAdSetFlows,
  expandAdSetsByFlow,
} from '../utils/adSetFlowCache';
import { restoreSelectedAdSetsFromUrl } from '../utils/adSetUrlSync';
import type { RestoredAdSetSelection } from '../utils/adSetUrlSync';
import {
  isDeepLinkStepPass,
  resolveFetchedStepStatus,
} from '../utils/deepLinkInit';
import type {
  DeepLinkDependentStepGate,
  DeepLinkStepResult,
} from './deepLinkTypes';

export interface DeepLinkAdSetsStepResult extends DeepLinkStepResult {
  restoredAdSets: RestoredAdSetSelection['selectedAdSets'];
  restoredFlows: string[];
  restoredAdSetsForPartnershipSettings: AdSettingsRequestAdSet[];
}

const useDeepLinkAdSetsStep = ({
  enabled,
  previousPass,
}: DeepLinkDependentStepGate): DeepLinkAdSetsStepResult => {
  const { setValue } = useFormContext();
  const {
    platform: platformParam,
    selectedCampaigns: selectedCampaignsParam,
    selectedAdSets: selectedAdSetsParam,
    filterAdSetsByName: filterAdSetsByNameParam,
  } = useLocationParams();

  const adSetCampaignIds = useMemo(
    () => selectedCampaignsParam.map((campaign) => campaign.id),
    [selectedCampaignsParam]
  );

  const adSetsQuery = useAdSets({
    params: {
      selectedCampaigns: adSetCampaignIds,
      platform: platformParam ?? '',
      query: filterAdSetsByNameParam,
    },
    config: {
      enabled: enabled && previousPass && adSetCampaignIds.length > 0,
    },
  });

  const missingEntries = useMemo(
    () =>
      adSetsQuery.data
        ? selectedAdSetsParam.filter(
            (adSet) =>
              !adSetsQuery.data!.some((row) => String(row.id) === adSet.id)
          )
        : [],
    [adSetsQuery.data, selectedAdSetsParam]
  );

  const missing = useMemo(
    () => missingEntries.map((adSet) => adSet.id),
    [missingEntries]
  );

  const restoredAdSetSelection = useMemo(() => {
    if (!adSetsQuery.data) return undefined;
    return restoreSelectedAdSetsFromUrl(selectedAdSetsParam, adSetsQuery.data);
  }, [adSetsQuery.data, selectedAdSetsParam]);

  const restoredAdSets = useMemo(
    () => restoredAdSetSelection?.selectedAdSets ?? [],
    [restoredAdSetSelection]
  );

  const restoredFlows = useMemo(
    () => collectUniqueAdSetFlows(restoredAdSets),
    [restoredAdSets]
  );

  const restoredAdSetsForPartnershipSettings = useMemo(
    () =>
      expandAdSetsByFlow(
        restoredAdSets.map(({ account_id, name, id, campaign_id, flow }) => ({
          account_id,
          name,
          id,
          campaign_id,
          flow: flow ?? '',
        }))
      ),
    [restoredAdSets]
  );

  const status = resolveFetchedStepStatus({
    enabled,
    previousPass,
    emptySuccess: adSetCampaignIds.length === 0,
    isPending: adSetsQuery.isPending,
    isError: adSetsQuery.isError,
    hasMissing: missingEntries.length > 0,
  });

  const restore = useCallback(() => {
    if (!enabled || !restoredAdSetSelection?.selectedAdSets.length) return;

    setValue('selectedAdSets', restoredAdSetSelection.selectedAdSets);
    setValue(
      'selectedAdSetsBudgets',
      restoredAdSetSelection.selectedAdSetsBudgets
    );
    setValue(
      'selectedAdSetsStartTimes',
      restoredAdSetSelection.selectedAdSetsStartTimes
    );
    setValue(
      'selectedAdSetsDuplicatesCount',
      restoredAdSetSelection.selectedAdSetsDuplicatesCount
    );
    setValue('selectedAdSetsFlows', restoredAdSetSelection.selectedAdSetsFlows);
  }, [enabled, restoredAdSetSelection, setValue]);

  return {
    status,
    pass: isDeepLinkStepPass(enabled, status),
    missing,
    restore,
    restoredAdSets,
    restoredFlows,
    restoredAdSetsForPartnershipSettings,
  };
};

export default useDeepLinkAdSetsStep;
